/** @format */
import isPropValid from '@emotion/is-prop-valid';
import { IconSize, IconVariant } from '@mpb-com/feather/components/atoms/icon';
import { animated } from '@react-spring/web';
import { useTranslation } from 'next-i18next';
import React, {
    ForwardRefRenderFunction,
    MutableRefObject,
    Ref,
    forwardRef,
    useContext,
    useRef,
} from 'react';
import styled from 'styled-components';

import CheckmarkIcon from '@toucan/client/global/icons/checkmark.svg';
import CloseIcon from '@toucan/client/global/icons/close.svg';
import PlusIcon from '@toucan/client/global/icons/plus.svg';

import { MarketContext } from '@toucan/client/global/market';
import {
    PriceModifier,
    preparePriceModifiers,
} from '@toucan/client/global/product';

import { GridRepeater } from '@toucan/client/shared/components/grid-repeater';
import { IconListItem } from '@toucan/client/shared/components/icon-list-item';

import { splitPriceModifiersIntoColumns } from './product-box.helpers';
import { styles } from './product-box.styles';
import { ProductBoxProps } from './types';
import { useBoxListAnimation } from './use-box-list-animation';

export const defaultTestId = 'product-box';

export const StyledProductBox = styled.div.withConfig({
    shouldForwardProp: (prop) => isPropValid(prop),
})`
    ${styles}
`;

const ProductBoxWithRef: ForwardRefRenderFunction<
    HTMLElement,
    ProductBoxProps
> = (
    {
        'data-testid': dataTestId = defaultTestId,
        className = 'product-box',
        isCollapsable = false,
        isCollapsed = true,
        columns = 1,
        priceModifiers,
        condition,
        maxItemsPerColumn,
    }: ProductBoxProps,
    ref: Ref<any> | null | undefined
) => {
    const boxListRef = useRef() as MutableRefObject<HTMLDivElement>;

    const { t } = useTranslation();
    const { activeMarket } = useContext(MarketContext);
    const boxListAnimatedContainerStyle = useBoxListAnimation(boxListRef, {
        isCollapsable,
        isCollapsed,
    });

    const { included, notIncluded } = preparePriceModifiers({
        t,
        priceModifiers,
        condition,
        activeMarket,
    });

    const boxList = [...included, ...notIncluded];

    /**
     * Renders an item in the box list
     */
    const renderIconListItem = ({ id, name, present }: PriceModifier) => {
        return (
            <IconListItem
                key={id}
                icon={
                    present ? (
                        <CheckmarkIcon
                            data-testid={`${dataTestId}__tick-icon`}
                            className="tick"
                        />
                    ) : (
                        <CloseIcon data-testid={`${dataTestId}__cross-icon`} />
                    )
                }
                isTransparent={!present}
                size={IconSize.XS}
                data-testid={`${dataTestId}__list-item`}
                className="list-item"
                variant={IconVariant.DECORATIVE}
            >
                {name}
            </IconListItem>
        );
    };

    /**
     * Renders the plus more items item
     *
     * @param totalHiddenPriceModifiers how many items are not being displayed
     */
    const renderPlusMoreItems = (totalHiddenPriceModifiers: number) => {
        return (
            <IconListItem
                icon={<PlusIcon data-testid={`${dataTestId}__plus-icon`} />}
                data-testid={`${dataTestId}__list-item`}
                className="more-items"
                size={IconSize.XS}
            >
                {totalHiddenPriceModifiers > 1
                    ? t('{{number}} more items', {
                          number: totalHiddenPriceModifiers,
                      })
                    : t('{{number}} more item', {
                          number: totalHiddenPriceModifiers,
                      })}
                ...
            </IconListItem>
        );
    };

    const { priceModifierColumns, totalHiddenPriceModifiers } =
        splitPriceModifiersIntoColumns(boxList, columns, maxItemsPerColumn);

    const shouldRenderPlusMoreItems = (columnIndex: number) => {
        const isLastColumn = columnIndex === priceModifierColumns.length - 1;
        return totalHiddenPriceModifiers > 1 && isLastColumn;
    };

    const renderSlicedPriceModifiers = (
        priceModifiers: PriceModifier[],
        index: number
    ) => {
        /**
         * When there are more items than will fit in the column.
         * We need remove the last item from the last column.
         * So that it can be replace with "plus more items".
         */
        const sliceEndIndex = shouldRenderPlusMoreItems(index) ? -1 : undefined;

        return priceModifiers.slice(0, sliceEndIndex).map(renderIconListItem);
    };

    return (
        <StyledProductBox
            ref={ref}
            data-testid={dataTestId}
            className={className}
            isCollapsable={isCollapsable}
        >
            <animated.div
                style={boxListAnimatedContainerStyle}
                className="list-animated-container"
                aria-hidden={isCollapsed}
            >
                <GridRepeater
                    ref={boxListRef}
                    gridColumns={columns}
                    className="list-container"
                >
                    {priceModifierColumns.map((priceModifiers, index) => (
                        <ul key={index} className="list">
                            {renderSlicedPriceModifiers(priceModifiers, index)}
                            {shouldRenderPlusMoreItems(index) &&
                                renderPlusMoreItems(totalHiddenPriceModifiers)}
                        </ul>
                    ))}
                </GridRepeater>
            </animated.div>
        </StyledProductBox>
    );
};

export const ProductBox = forwardRef(ProductBoxWithRef);
